import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography
} from '@mui/material';

import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';

import TablePaginationActions from '../../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../../components/hooks/useTablePaginationInit';
import useTableSortInit from '../../../../components/hooks/useTableSortInit';
import SummaryTiles, { TILE_ACCENT } from '../../../../components/utils/SummaryTiles';
import TableTimeStamps from '../../../../components/utils/TableTimeStamps';
import Copyable from '../../../../components/utils/Copyable';
import TopBar from '../../../../components/utils/TopBar';
import NA from '../../../../components/utils/NA';

import {
  GetInternalUsersBIZidentity
} from '../../../../redux/actions/business/identity/internalUsersActions';

const InternalUsersPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.identity"]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [internalUsersList, setInternalUsersList] = useState([]);
  const [internalUsersTotal, setInternalUsersTotal] = useState(0);
  const [summary, setSummary] = useState(null);

  const [
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  const [
    sort,
    sortDirection,
    handleSort
  ] = useTableSortInit();

  // searching from a later page would land on an empty result set
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    handleChangePage(null, 0);
  };

  const tableHeadingsList = [
    {
      label: "Id",
      sort: "id",
    },
    {
      label: "Username",
      sort: "user_name",
    },
    {
      label: "Name",
      sort: "first_name",
    },
    {
      label: "Email",
      sort: "primary_email",
    },
    {
      label: "Login allowed",
      sort: "is_login_allowed",
    },
    {
      label: "MFA",
      sort: "mfa_enabled",
    },
    {
      label: "Time stamps",
      sort: "createdAt",
    }
  ];

  useEffect(() => {
    handleGetInternalUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection]);

  useEffect(() => {
    try {
      setInternalUsersList(state.internalUsersList?.records || []);
      setInternalUsersTotal(state.internalUsersList?.total || 0);
      setSummary(state.internalUsersList?.summary || null);
    } catch (err) {}
  }, [state]);

  async function handleGetInternalUsers(customLoading){
    let query = "";
    if(search) query += `&search=${search}`;
    if(sort) query += `&field=${sort}`;
    if(sortDirection) query += `&order=${sortDirection}`;
    await dispatch(GetInternalUsersBIZidentity(page+1, rowsPerPage, query, true, customLoading ? customLoading : setLoading));
  };

  const summaryTiles = [
    { label: "Internal users", value: Number(summary?.total || 0), accent: TILE_ACCENT.neutral },
    { label: "MFA enabled", value: Number(summary?.mfa_enabled || 0), accent: TILE_ACCENT.good },
    { label: "MFA not set up", value: Number(summary?.mfa_disabled || 0), accent: TILE_ACCENT.warn },
    { label: "Login disabled", value: Number(summary?.login_disabled || 0), accent: TILE_ACCENT.bad },
  ];

  return (
    <Box>

      <TopBar
        title={"Internal Users"}
        search={search}
        searchPlaceholder={"Search id, username, name or email"}
        onSearchChange={handleSearchChange}
        button1Text={"Refresh"}
        button1OnClick={() => {
          handleGetInternalUsers();
        }}
        />

      <SummaryTiles tiles={summaryTiles} />

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          internalUsersList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No internal user found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell
                      key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell
                      key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort ? true : false}
                        direction={sortDirection}
                        onClick={() => {
                          handleSort(heading.sort);
                        }}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                internalUsersList.map((row, index) => (
                  <TableRow
                    key={index}>
                    <TableCell>
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        <Copyable>
                          {row.id || <NA />}
                        </Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Copyable>
                        {row.user_name || <NA />}
                      </Copyable>
                    </TableCell>
                    <TableCell>
                      <Typography>
                        {row.first_name || <NA />}&nbsp;{row.last_name || ""}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Copyable>
                        {row.primary_email || <NA />}
                      </Copyable>
                    </TableCell>
                    <TableCell>
                      {
                        row.is_login_allowed === true
                        ? <CheckCircleOutlineOutlinedIcon color='success'/>
                        : <CancelOutlinedIcon color='error'/>
                      }
                    </TableCell>
                    <TableCell>
                      <Chip
                        size='small'
                        label={row.mfa_enabled ? "Enabled" : "Not set up"}
                        color={row.mfa_enabled ? "success" : "warning"}
                        variant={row.mfa_enabled ? "filled" : "outlined"}
                        />
                    </TableCell>
                    <TableCell>
                      <TableTimeStamps
                        createdAt={row.createdAt}
                        updatedAt={row.updatedAt}
                        />
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  colSpan={10}
                  count={internalUsersTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'rows per page'
                    },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                  />
              </TableRow>
            </TableFooter>
          </Table>
      }
    </Box>
  )
}

export default InternalUsersPage;
